import React, { useState, useEffect } from "react";
import { useReturnToMainCenterMutation, useGetInventoriesQuery } from "../api/starterKitApi";
import { showToast } from "../../../core/utils/alert";
import CommonSelect from "../../../core/common/commonSelect";
import { getApiErrorMessage } from "../../../core/utils/errorHandler";
import { useCourse } from "../../configuration/course/hooks/useCourse";

interface ReturnStockModalProps {
  initialData?: {
    center_id: string;
    center_name: string;
    available_stock: number;
  } | null;
  onClose?: () => void;
}

const ReturnStockModal = ({ initialData, onClose }: ReturnStockModalProps) => {
  const [formData, setFormData] = useState({
    course_id: "",
    quantity: 0,
    center_id: "",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        course_id: "",
        quantity: 0,
        center_id: initialData.center_id,
      });
    }
  }, [initialData]);

  const { data: inventoryData } = useGetInventoriesQuery({ size: 100 });
  const [returnToMainCenter, { isLoading }] = useReturnToMainCenterMutation();

  const { getCourseState } = useCourse();
  const { courses } = getCourseState();
  const courseOptions = courses.map(course => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const inventories = inventoryData?.data?.items || [];
  // Only show centers that have stock and are not the main center and match selected course
  const centerOptions = inventories
    .filter((item: any) => !item.is_main_center_stock && item.available_stock > 0 && (!formData.course_id || Number(item.course_id) === Number(formData.course_id) || Number(item.course?.id) === Number(formData.course_id)))
    .map((item: any) => ({
      value: item.center?.id.toString(),
      label: `${item.center?.name} (Available: ${item.available_stock})`,
      available: item.available_stock
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.course_id) {
      showToast({ title: "Error", text: "Please select a course", icon: "error" });
      return;
    }
    if (!formData.center_id) {
      showToast({ title: "Error", text: "Please select a center", icon: "error" });
      return;
    }

    const selectedCenter = centerOptions.find(opt => opt.value === formData.center_id);
    if (selectedCenter && formData.quantity > selectedCenter.available) {
      showToast({
        title: "Error",
        text: `Quantity cannot exceed available stock (${selectedCenter.available})`,
        icon: "error"
      });
      return;
    }

    try {
      const response = await returnToMainCenter({
        center_id: Number(formData.center_id),
        course_id: Number(formData.course_id),
        quantity: formData.quantity,
      }).unwrap();

      if (response.success) {
        showToast({
          title: "Success",
          text: "Stock returned to Main Center successfully",
          icon: "success",
        });
        // Reset form
        setFormData({
          course_id: "",
          quantity: 0,
          center_id: "",
        });
        // Close modal
        handleModalClose();
      }
    } catch (error: any) {
      console.error("Return error:", error);
      showToast({
        title: "Error",
        text: getApiErrorMessage(error) || "Failed to return stock",
        icon: "error",
      });
    }
  };

  const handleModalClose = () => {
    const modalElement = document.getElementById("return_stock_modal");
    if (modalElement) {
      const bootstrap = (window as any).bootstrap;
      if (bootstrap && bootstrap.Modal) {
        const modal = bootstrap.Modal.getInstance(modalElement);
        modal?.hide();
      } else {
        const closeButton = modalElement.querySelector('[data-bs-dismiss="modal"]') as HTMLButtonElement;
        closeButton?.click();
      }
    }
    if (onClose) onClose();
  };

  return (
    <div className="modal fade" id="return_stock_modal">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Return Stock from Center</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
              onClick={onClose}
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Course</label>
                    <CommonSelect
                      options={courseOptions}
                      value={courseOptions.find(opt => opt.value === formData.course_id)}
                      onValueChange={(val) => setFormData({ ...formData, course_id: val })}
                      placeholder="Select Course"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Center</label>
                    <CommonSelect
                      options={centerOptions}
                      value={centerOptions.find(opt => opt.value === formData.center_id)}
                      onValueChange={(val) => setFormData({ ...formData, center_id: val })}
                      placeholder="Select Center"
                      required
                      isDisabled={!!initialData}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Quantity</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.quantity || ""}
                      onChange={(e) => setFormData({
                        ...formData,
                        quantity: Number(e.target.value)
                      })}
                      required
                      min="1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
                onClick={onClose}
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? "Processing..." : "Return Stock"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ReturnStockModal;
